// src/services/profileDataService.js
import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  collection,
  serverTimestamp,
} from "firebase/firestore";

import { db, storage } from "../firebase";
import {
  isNativeFirestorePlatform,
  getPlatform,
  extractNativeDocumentData,
  extractNativeDocuments,
} from "./firestorePlatform";

import { getCurrentAuthUser } from "./authDataService";

const USERS_COLLECTION = "nutzer";
const OFFERS_COLLECTION = "angebote";

const userRefPath = (uid) => `${USERS_COLLECTION}/${uid}`;
const offerRefPath = (offerId) => `${OFFERS_COLLECTION}/${offerId}`;
const userOffersCollectionPath = (uid) => `${USERS_COLLECTION}/${uid}/angebote`;

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeOffer = (id, data) => ({
  id,
  ...(normalizeObject(data)),
});

const extractNativeCollectionDocs = (result) => {
  const docs = extractNativeDocuments(result);
  if (Array.isArray(docs) && docs.length > 0) return docs;

  const fallback = result?.snapshots;
  return Array.isArray(fallback) ? fallback : [];
};

const mapNativeOfferDocs = (result) => {
  const docs = extractNativeCollectionDocs(result);

  return docs.map((item, index) => {
    const id =
      item?.id ||
      item?.documentId ||
      item?.snapshot?.id ||
      item?.document?.id ||
      (typeof item?.path === "string" ? item.path.split("/").pop() : null) ||
      `offer-${index}`;

    const data =
      item?.data ||
      item?.snapshot?.data ||
      item?.document?.data ||
      item?.value ||
      item?.fields ||
      {};

    return normalizeOffer(id, data);
  });
};

// -----------------------------------------------------
// USER PROFILE
// -----------------------------------------------------

const getUserProfileViaWeb = async (uid) => {
  const snap = await getDoc(doc(db, USERS_COLLECTION, uid));
  return snap.exists() ? normalizeObject(snap.data()) : null;
};

const getUserProfileViaNative = async (uid) => {
  const result = await FirebaseFirestore.getDocument({
    reference: userRefPath(uid),
  });

  const data = extractNativeDocumentData(result);
  const hasData = data && Object.keys(data).length > 0;

  if (!hasData) return null;
  return normalizeObject(data);
};

export const getUserProfileData = async (uid) => {
  if (!uid) return null;

  if (isNativeFirestorePlatform()) {
    console.log("🟢 getUserProfileData via Native", {
      platform: getPlatform(),
      uid,
    });
    return await getUserProfileViaNative(uid);
  }

  console.log("🟢 getUserProfileData via Web", { uid });
  return await getUserProfileViaWeb(uid);
};

const buildProfileSavePayload = (data = {}) => {
  const payload = {
    ...normalizeObject(data),
  };

  if (payload.email) {
    payload.email = normalizeEmail(payload.email);
  }

  if (isNativeFirestorePlatform()) {
    payload.updatedAtClient = new Date().toISOString();
    return payload;
  }

  payload.updatedAt = serverTimestamp();
  return payload;
};

const saveUserProfileViaWeb = async (uid, data) => {
  await setDoc(doc(db, USERS_COLLECTION, uid), buildProfileSavePayload(data), {
    merge: true,
  });
};

const saveUserProfileViaNative = async (uid, data) => {
  await FirebaseFirestore.setDocument({
    reference: userRefPath(uid),
    data: buildProfileSavePayload(data),
    merge: true,
  });
};

export const saveUserProfileData = async (uid, data) => {
  if (!uid) throw new Error("saveUserProfileData: uid fehlt");

  if (isNativeFirestorePlatform()) {
    console.log("🟢 saveUserProfileData via Native", {
      platform: getPlatform(),
      uid,
    });
    return await saveUserProfileViaNative(uid, data);
  }

  console.log("🟢 saveUserProfileData via Web", { uid });
  return await saveUserProfileViaWeb(uid, data);
};

export const createOrMergeBasicUserProfile = async ({
  uid,
  email = "",
  username = "",
  name = "",
}) => {
  if (!uid) {
    throw new Error("createOrMergeBasicUserProfile: uid fehlt");
  }

  const finalEmail = normalizeEmail(email);
  const finalName = String(name || username || "").trim();

  const payload = {
    email: finalEmail,
    name: finalName,
    username: finalName,
  };

  if (isNativeFirestorePlatform()) {
    payload.createdAtClient = new Date().toISOString();
    payload.updatedAtClient = new Date().toISOString();

    await FirebaseFirestore.setDocument({
      reference: userRefPath(uid),
      data: payload,
      merge: true,
    });
    return;
  }

  await setDoc(
    doc(db, USERS_COLLECTION, uid),
    {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

export const getUserDisplayName = async (uid) => {
  if (!uid) return "Unbekannt";

  try {
    const profile = await getUserProfileData(uid);
    if (profile?.name) return profile.name;
    if (profile?.username) return profile.username;
  } catch (error) {
    console.warn("📘 getUserDisplayName fallback:", error);
  }

  return "Unbekannt";
};

export const getCurrentAppUser = async () => {
  const user = await getCurrentAuthUser();
  if (!user?.uid) return null;

  let profile = null;

  try {
    profile = await getUserProfileData(user.uid);
  } catch (error) {
    console.warn("📘 getCurrentAppUser profile load failed:", error);
  }

  return {
    uid: user.uid,
    email: user.email || "",
    displayName:
      profile?.name ||
      profile?.username ||
      user.displayName ||
      user.email ||
      "Unbekannt",
  };
};

// -----------------------------------------------------
// OFFERS
// -----------------------------------------------------

const getMyOffersViaWeb = async (uid) => {
  const qOffers = query(collection(db, OFFERS_COLLECTION), where("userId", "==", uid));
  const snap = await getDocs(qOffers);

  if (!snap.empty) {
    return snap.docs.map((d) => normalizeOffer(d.id, d.data()));
  }

  const subSnap = await getDocs(collection(db, userOffersCollectionPath(uid)));
  return subSnap.docs.map((d) => normalizeOffer(d.id, d.data()));
};

const getMyOffersViaNative = async (uid) => {
  const result = await FirebaseFirestore.getCollection({
    reference: OFFERS_COLLECTION,
  });

  const allOffers = mapNativeOfferDocs(result);
  const myOffers = allOffers.filter(
    (offer) => String(offer?.userId || "") === String(uid)
  );

  if (myOffers.length > 0) return myOffers;

  const fallback = await FirebaseFirestore.getCollection({
    reference: userOffersCollectionPath(uid),
  });

  return mapNativeOfferDocs(fallback);
};

export const getMyOffersData = async (uid) => {
  if (!uid) return [];

  if (isNativeFirestorePlatform()) {
    console.log("🟢 getMyOffersData via Native", {
      platform: getPlatform(),
      uid,
    });
    return await getMyOffersViaNative(uid);
  }

  console.log("🟢 getMyOffersData via Web", { uid });
  return await getMyOffersViaWeb(uid);
};

const getAllOffersViaWeb = async () => {
  const snap = await getDocs(collection(db, OFFERS_COLLECTION));
  return snap.docs.map((d) => normalizeOffer(d.id, d.data()));
};

const getAllOffersViaNative = async () => {
  const result = await FirebaseFirestore.getCollection({
    reference: OFFERS_COLLECTION,
  });

  return mapNativeOfferDocs(result);
};

export const getOfferByIdData = async (angebotId) => {
  if (!angebotId) return null;

  if (isNativeFirestorePlatform()) {
    try {
      const result = await FirebaseFirestore.getDocument({
        reference: offerRefPath(String(angebotId)),
      });

      const data = extractNativeDocumentData(result);
      if (!data || Object.keys(data).length === 0) return null;

      return { id: String(angebotId), ...data };
    } catch (error) {
      console.warn("Native getOfferById failed:", error);
      return null;
    }
  }

  try {
    const refDoc = doc(db, OFFERS_COLLECTION, String(angebotId));
    const snap = await getDoc(refDoc);

    if (!snap.exists()) return null;

    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.error("Web getOfferById failed:", error);
    return null;
  }
};

export const getSavedOffersData = async (uid) => {
  if (!uid) return [];

  const profile = await getUserProfileData(uid);
  const savedIds = Array.isArray(profile?.savedOfferIds) ? profile.savedOfferIds : [];

  if (!savedIds.length) return [];

  const allOffers = isNativeFirestorePlatform()
    ? await getAllOffersViaNative()
    : await getAllOffersViaWeb();

  return allOffers.filter((offer) => savedIds.includes(offer.id));
};

export const saveOfferForUserData = async (uid, offerId) => {
  if (!uid || !offerId) return;

  const profile = (await getUserProfileData(uid)) || {};
  const currentSavedIds = Array.isArray(profile.savedOfferIds) ? profile.savedOfferIds : [];

  if (currentSavedIds.includes(String(offerId))) return;

  await saveUserProfileData(uid, {
    savedOfferIds: [...currentSavedIds, String(offerId)],
  });
};

export const removeSavedOfferData = async (uid, offerId) => {
  if (!uid || !offerId) return;

  const profile = (await getUserProfileData(uid)) || {};
  const currentSavedIds = Array.isArray(profile.savedOfferIds) ? profile.savedOfferIds : [];
  const nextSavedIds = currentSavedIds.filter((id) => String(id) !== String(offerId));

  await saveUserProfileData(uid, { savedOfferIds: nextSavedIds });
};

const deleteOfferViaWeb = async (uid, offerId) => {
  try {
    await deleteDoc(doc(db, OFFERS_COLLECTION, String(offerId)));
  } catch (error) {
    console.warn('Konnte Angebot in "angebote" nicht löschen, versuche Subcollection:', error);
    await deleteDoc(doc(db, userOffersCollectionPath(uid), String(offerId)));
  }
};

const deleteOfferViaNative = async (uid, offerId) => {
  try {
    await FirebaseFirestore.deleteDocument({
      reference: offerRefPath(String(offerId)),
    });
  } catch (error) {
    console.warn('Native: Konnte Angebot in "angebote" nicht löschen, versuche Subcollection:', error);
    await FirebaseFirestore.deleteDocument({
      reference: `${userOffersCollectionPath(uid)}/${String(offerId)}`,
    });
  }
};

export const deleteOfferData = async (uid, offerId) => {
  if (!uid || !offerId) {
    throw new Error("deleteOfferData: uid oder offerId fehlt");
  }

  if (isNativeFirestorePlatform()) {
    console.log("🟢 deleteOfferData via Native", {
      platform: getPlatform(),
      uid,
      offerId,
    });
    return await deleteOfferViaNative(uid, offerId);
  }

  console.log("🟢 deleteOfferData via Web", { uid, offerId });
  return await deleteOfferViaWeb(uid, offerId);
};

// -----------------------------------------------------
// PROFILE IMAGE
// -----------------------------------------------------

export const uploadProfileImageData = async (uid, file) => {
  if (!uid || !file) {
    throw new Error("uploadProfileImageData: uid oder file fehlt");
  }

  const safeName = (file.name || "image").replace(/\s+/g, "_");
  const path = `profileImages/${uid}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);

  await saveUserProfileData(uid, { profileImageURL: downloadURL });

  return downloadURL;
};

export const clearProfileImageData = async (uid) => {
  if (!uid) return;
  await saveUserProfileData(uid, { profileImageURL: "" });
};